import { ref, computed, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { fetchPendingCountFamilias, getPendingCountFamilias } from '@/composables/admin/abonados/useFamilias';
import { useRoute } from '@/composables/common/router/useRouter';
import { TEATRO_HASH_SANT_ANTONI } from '@/constants/teatros';

export const TERRAIN_TEATRO_HASH = 'J1fKG9AarPuPNJ5hwY0t';

function capitalizeFirst(value) {
    const text = String(value || '').trim();
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Build the admin navigation model (3 groups) from the current store context.
 * Pure function: every permission/label rule from the legacy AsideComponent
 * is preserved here and unit-tested against a context fixture.
 *
 * @param {object} context
 * @returns {Array<{key: string, label: string, items: Array<object>}>}
 */
export function buildAdminNavGroups(context = {}) {
    const permisos = context.permisos || {};
    const manager = context.manager || {};
    const teatro = context.teatro || {};
    const configuracion = context.configuracion || {};
    const listaAbonados = context.listaAbonados || {};
    const listaId = context.listaId;
    const user = context.user || {};
    const pendingFamiliasCount = context.pendingFamiliasCount || 0;
    const numMensajes = context.numMensajes || 0;

    // El taquillero del club visitante (rol 11) no ve la dashboard del club local.
    const isTaquilleroVisitante = user.role === 11;
    const terrenoLabel = teatro.hash === TERRAIN_TEATRO_HASH ? 'Terrenos' : 'Parcelas';
    const terrenoVendidosLabel = terrenoLabel === 'Terrenos' ? 'Terrenos vendidos' : 'Parcelas vendidas';
    const simpatizantesLabel = configuracion.simpatizantes || 'simpatizantes';

    return [
        {
            key: 'sales',
            label: 'Ventas & Taquilla',
            items: [
                {
                    key: 'dashboard',
                    label: 'Dashboard',
                    icon: 'fas fa-tachometer-alt',
                    route: { name: 'back.dashboard.club' },
                    visible: teatro.division_id == 3 && !isTaquilleroVisitante
                },
                {
                    key: 'abonos',
                    label: 'Venta de abonos',
                    icon: 'fas fa-id-card-alt',
                    route: { name: 'back.abonos.index' },
                    visible: !manager.lfp
                        && !!(permisos['abonos.vender'] || permisos['abonos.informe'])
                        && (teatro.hash !== TEATRO_HASH_SANT_ANTONI || manager.tipo !== 2 || !manager.ocultar_abonos)
                },
                {
                    key: 'simpatizantes-venta',
                    label: 'Venta de ' + simpatizantesLabel,
                    icon: 'fas fa-hands-helping',
                    route: { name: 'back.simpatizantes.venta' },
                    visible: manager.tipo == 1 && !manager.lfp && !!permisos['abonos.vender']
                },
                {
                    key: 'events',
                    label: 'Eventos',
                    icon: 'fas fa-calendar-week',
                    route: { name: 'back.events.index' },
                    visible: !!permisos['eventos.vender']
                },
                {
                    key: 'parcelas',
                    label: terrenoLabel,
                    icon: 'fas fa-th',
                    visible: !!listaAbonados.activar_parcelas,
                    children: [
                        { key: 'parcelas-gestion', label: 'Gestión de ' + terrenoLabel, icon: 'fas fa-layer-group', route: { name: 'back.precios.parcelas.index' }, visible: true },
                        { key: 'parcelas-venta', label: 'Venta', icon: 'fas fa-shopping-cart', route: { name: 'back.venta.parcelas' }, visible: true },
                        { key: 'parcelas-vendidas', label: terrenoVendidosLabel, icon: 'fas fa-file-contract', route: { name: 'back.listado.parcelas.vendidas' }, visible: true },
                        { key: 'parcelas-informe', label: 'Informe', icon: 'fas fa-chart-bar', route: { name: 'back.listado.parcelas.informe' }, visible: true }
                    ]
                }
            ]
        },
        {
            key: 'community',
            label: 'Comunidad & Socios',
            items: [
                {
                    key: 'abonados',
                    label: 'Abonados',
                    icon: 'fas fa-address-card',
                    route: { name: 'back.abonados.index', params: { lista_id: listaId } },
                    visible: !!permisos['abonos.vender']
                },
                {
                    key: 'familias',
                    label: 'Familias',
                    icon: 'fas fa-users',
                    route: { name: 'back.familias.index', params: { lista_id: listaId }, query: { estado: 'pendiente' } },
                    visible: !!teatro.familia_abonados_activa && pendingFamiliasCount > 0,
                    badge: pendingFamiliasCount
                },
                {
                    key: 'envios',
                    label: 'Envíos',
                    icon: 'fas fa-shipping-fast',
                    route: { name: 'back.envios.index' },
                    visible: !!permisos['abonos.vender']
                },
                {
                    key: 'simpatizantes',
                    label: capitalizeFirst(simpatizantesLabel),
                    icon: 'fas fa-handshake',
                    route: { name: 'back.simpatizantes.index' },
                    visible: manager.tipo == 1 && !!permisos['abonos.vender'],
                    domId: 'navbar-simpatizantes'
                },
                {
                    key: 'deportistas',
                    label: 'Deportistas',
                    icon: 'fas fa-child',
                    visible: !!permisos['bases.informe'],
                    children: [
                        { key: 'deportistas-listado', label: 'Listado', icon: 'fas fa-list', route: { name: 'back.bases.index', params: { lista_id: listaId } }, visible: true },
                        { key: 'deportistas-indumentarias', label: 'Recogida indumentarias', icon: 'fas fa-tshirt', route: { name: 'back.bases.recogida-indumentarias', params: { lista_id: listaId } }, visible: true }
                    ]
                }
            ]
        },
        {
            key: 'management',
            label: 'Gestión & Operativa',
            items: [
                {
                    key: 'usuarios',
                    label: 'Usuarios',
                    icon: 'fas fa-users-cog',
                    route: { name: 'back.taquilleros.index' },
                    visible: !!permisos['taquilleros.gestion']
                },
                {
                    key: 'liquidaciones',
                    label: 'Liquidaciones',
                    icon: 'fas fa-calculator',
                    visible: !!permisos['liquidaciones'],
                    children: [
                        { key: 'liquidaciones-abonos', label: 'Abonos', icon: 'fas fa-ticket-alt', route: { name: 'back.liquidaciones.abonos.index' }, visible: true },
                        { key: 'liquidaciones-eventos', label: 'Eventos', icon: 'fas fa-calendar-check', route: { name: 'back.liquidaciones.eventos.index' }, visible: true },
                        { key: 'liquidaciones-productos', label: 'Productos', icon: 'fas fa-box', route: { name: 'back.liquidaciones.productos.index' }, visible: true },
                        { key: 'liquidaciones-cantera', label: 'Cantera', icon: 'fas fa-futbol', route: { name: 'back.liquidaciones.bases.index' }, visible: true },
                        { key: 'liquidaciones-parcelas', label: terrenoLabel, icon: 'fas fa-th', route: { name: 'back.liquidaciones.parcelas.index' }, visible: true },
                        { key: 'liquidaciones-facturas', label: 'Facturas comisiones bancarias', icon: 'fas fa-file-invoice', route: { name: 'back.facturas.index' }, visible: true }
                    ]
                },
                {
                    key: 'marketing',
                    label: 'Marketing',
                    icon: 'fas fa-bullhorn',
                    visible: !!permisos['comunicaciones'],
                    children: [
                        { key: 'marketing-comunicados', label: 'Comunicados', icon: 'fas fa-rss', route: { name: 'back.comunicados.index' }, visible: true },
                        { key: 'marketing-whatsapp', label: 'Plantillas WhatsApp', icon: 'fab fa-whatsapp', route: { name: 'back.plantillas-whatsapp.index' }, visible: true },
                        { key: 'marketing-upselling', label: 'Upselling', icon: 'fas fa-chart-line', route: { name: 'back.upselling.index' }, visible: true }
                    ]
                },
                {
                    key: 'emails',
                    label: 'Campaña Emails',
                    icon: 'fas fa-envelope',
                    visible: !!teatro.marketing_api_token,
                    children: [
                        { key: 'emails-dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt', route: { name: 'back.emails.dashboard' }, visible: true },
                        { key: 'emails-subscriptores', label: 'Subscriptores', icon: 'fas fa-users', route: { name: 'back.emails.subscriptores.index' }, visible: true },
                        { key: 'emails-grupos', label: 'Grupos', icon: 'fas fa-users-cog', route: { name: 'back.emails.grupos.index' }, visible: true },
                        { key: 'emails-importaciones', label: 'Importaciones', icon: 'fas fa-file-import', route: { name: 'back.emails.importaciones' }, visible: true },
                        { key: 'emails-boletines', label: 'Boletines', icon: 'fas fa-newspaper', route: { name: 'back.emails.boletines.index' }, visible: true },
                        { key: 'emails-remitentes', label: 'Remitentes', icon: 'fas fa-envelope', route: { name: 'back.emails.remitentes.index' }, visible: true },
                        { key: 'emails-plantillas', label: 'Plantillas', icon: 'fas fa-shapes', route: { name: 'back.emails.plantilla.index' }, visible: true },
                        { key: 'emails-boletines-enviados', label: 'Boletines enviados', icon: 'fas fa-paper-plane', route: { name: 'back.emails.informes.boletines-enviados' }, visible: true },
                        { key: 'emails-medios', label: 'Gestor de medios', icon: 'fas fa-photo-video', route: { name: 'back.emails.gestor-de-medios' }, visible: true }
                    ]
                },
                {
                    key: 'mensajes',
                    label: 'Mensajes',
                    icon: 'far fa-comment-dots',
                    route: { name: 'back.mensajes.index' },
                    visible: !!permisos['taquilleros.gestion'],
                    badge: numMensajes
                },
                {
                    key: 'acreditaciones',
                    label: 'Acreditaciones',
                    icon: 'fas fa-ticket-alt',
                    route: { name: 'back.acreditaciones.index', params: { lista_id: listaId } },
                    visible: !!permisos['taquilleros.gestion']
                },
                {
                    key: 'configuracion',
                    label: 'Configuración',
                    icon: 'fas fa-cogs',
                    visible: !!permisos['taquilleros.gestion'],
                    children: [
                        { key: 'config-general', label: 'General', icon: 'fas fa-sliders-h', route: { name: 'back.configuracion.index' }, visible: true },
                        { key: 'config-formularios', label: 'Formularios', icon: 'fas fa-clipboard', route: { name: 'back.formularios.index' }, visible: true },
                        { key: 'config-campana-abonos', label: 'Campaña de abonados', icon: 'fas fa-newspaper', route: { name: 'back.campana-abonos.index' }, visible: true },
                        { key: 'config-compania-eventos', label: 'Compañia eventos', icon: 'fas fa-calendar-week', route: { name: 'back.compania-eventos.index' }, visible: true },
                        { key: 'config-deportistas', label: 'Deportistas', icon: 'fas fa-child', route: { name: 'back.deportistas.index' }, visible: true },
                        { key: 'config-productos', label: 'Productos', icon: 'fas fa-box', route: { name: 'back.productos.index' }, visible: true },
                        { key: 'config-etiquetas', label: 'Etiquetas de bloqueos', icon: 'fas fa-tag', route: { name: 'back.etiquetas-bloqueos.index' }, visible: true }
                    ]
                },
                {
                    key: 'faqs',
                    label: 'Preguntas frecuentes',
                    icon: 'fas fa-question',
                    route: { name: 'back.faqs.index' },
                    visible: true
                }
            ]
        }
    ];
}

/**
 * Filter hidden items/children/groups and drop toggle parents left with no
 * visible children. Pure function over `buildAdminNavGroups` output.
 * @param {Array<object>} groups
 * @returns {Array<object>}
 */
export function visibleAdminNavGroups(groups = []) {
    return groups
        .map(group => ({
            ...group,
            items: group.items
                .filter(item => item.visible !== false)
                .map(item => item.children
                    ? { ...item, children: item.children.filter(child => child.visible !== false) }
                    : item)
                .filter(item => !item.children || item.children.length > 0)
        }))
        .filter(group => group.items.length > 0);
}

/**
 * Route-name prefix used to keep an item active on its detail pages
 * (e.g. `back.events.index` -> `back.events` also matches `back.events.crear`).
 * @param {object} item
 * @returns {string|null}
 */
export function routeMatchPrefix(item) {
    if (!item.route || !item.route.name) return null;
    return item.route.name.replace(/\.index$/, '');
}

/**
 * Is the given route name within the item's route scope?
 * @param {object} item
 * @param {string} routeName
 * @returns {boolean}
 */
export function isRouteWithinItem(item, routeName) {
    if (!routeName || !item.route || !item.route.name) return false;
    if (item.route.name === routeName) return true;
    const prefix = routeMatchPrefix(item);
    return !!prefix && routeName.startsWith(prefix + '.');
}

function isBetterMatch(candidate, current, routeName) {
    const candidateExact = candidate.item.route.name === routeName;
    const currentExact = current.item.route.name === routeName;
    if (candidateExact !== currentExact) return candidateExact;
    return candidate.item.route.name.length > current.item.route.name.length;
}

/**
 * Deepest active chain for a route name: `{ group, item, ancestors }` or null.
 * Exact name match wins; otherwise the longest prefix wins.
 * @param {Array<object>} groups
 * @param {string} routeName
 * @returns {{group: object, item: object, ancestors: Array<object>}|null}
 */
export function findActiveChain(groups = [], routeName) {
    if (!routeName) return null;
    let best = null;

    function considerCandidate(candidate) {
        if (!isRouteWithinItem(candidate.item, routeName)) return;
        if (!best || isBetterMatch(candidate, best, routeName)) {
            best = candidate;
        }
    }

    groups.forEach(group => {
        group.items.forEach(item => {
            considerCandidate({ group, item, ancestors: [] });
            (item.children || []).forEach(child => {
                considerCandidate({ group, item: child, ancestors: [item] });
            });
        });
    });

    return best;
}

/**
 * Etiquetas exactas por nombre de ruta. Es la fuente única para los tramos que
 * no se pueden derivar del identificador (mayúsculas, acentos, preposiciones)
 * y para las rutas cuyo último segmento es `index`.
 */
const BREADCRUMB_ROUTE_LABELS = {
    'back.events.crear': 'Crear evento',
    'back.events.editar': 'Editar evento',
    'back.events.compania': 'Compañía',
    'back.events.fields': 'Campos personalizados',
    'back.events.sales-ad.index': 'Sales AD',
    'back.events.preventa.index': 'Solicitudes preventa',
    'back.events.informe.emails': 'Estadísticas de emails',
    'back.events.informe.emails.listado': 'Listado de emails',
    'back.events.sesion.reservas.show': 'Detalle de reserva',
    'back.events.sesion.informe.control-acceso': 'Control de acceso',
    'back.events.sesion.control-acceso': 'Control de acceso',
    'back.events.sesion.codigos-qr': 'Códigos QR',
    'back.events.sesion.listado-bloqueos': 'Listado de bloqueos',
    'back.events.sesion.compradores-permitidos.add': 'Añadir comprador'
};

/**
 * Etiquetas para el último tramo cuando la ruta no está en el menú ni tiene
 * etiqueta exacta. Sin esto, el tramo saldría con el identificador de la ruta
 * ("show", "configuracion").
 */
const BREADCRUMB_TAIL_LABELS = {
    crear: 'Crear',
    editar: 'Editar',
    update: 'Editar',
    show: 'Detalle',
    compania: 'Compañía',
    configuracion: 'Configuración',
    reservas: 'Reservas',
    ventas: 'Ventas',
    informe: 'Informe',
    productos: 'Productos',
    preventa: 'Preventa',
    sesion: 'Sesión'
};

/**
 * Etiqueta del tramo que añade la ruta actual sobre la página del menú.
 * Prioridad: `meta.breadcrumb` (etiqueta explícita) > mapa de segmentos >
 * segmento humanizado.
 *
 * @param {object} route
 * @param {object} item deepest matching nav item
 * @returns {string|null}
 */
function breadcrumbTail(route, item) {
    const metaLabel = route.meta && route.meta.breadcrumb;
    if (metaLabel) return metaLabel;

    const itemRouteName = item.route && item.route.name;
    if (!route.name || route.name === itemRouteName) return null;

    const routeLabel = BREADCRUMB_ROUTE_LABELS[route.name];
    if (routeLabel) return routeLabel;

    // Rutas cuyo último segmento es `index` (p. ej. `puertas.index`) usan el
    // segmento anterior: el `index` identifica la vista, no la sección.
    const segments = String(route.name).split('.').filter(segment => segment !== 'index');
    const segment = segments[segments.length - 1];
    if (!segment) return null;

    return BREADCRUMB_TAIL_LABELS[segment] || capitalizeFirst(segment.replace(/-/g, ' '));
}

/**
 * Breadcrumb items for the header: [grupo, página del menú, página actual].
 * El tercer nivel solo aparece cuando la ruta actual es más profunda que la
 * página del menú (detalle/crear/editar y demás subpáginas).
 *
 * Cada tramo es `{ label, to }`. `to` es una location de vue-router cuando el
 * tramo se puede navegar (la página del menú, solo si hay un tercer nivel);
 * el grupo no es una ruta y la página actual tampoco se enlaza.
 *
 * Acepta el nombre de ruta (compatibilidad con los tests existentes) o el
 * objeto `$route` completo, que es lo que pasa el header para poder leer
 * `meta.breadcrumb`.
 *
 * @param {Array<object>} groups
 * @param {string|object} routeOrName
 * @returns {Array<{label: string, to: object|null}>}
 */
export function resolveBreadcrumb(groups = [], routeOrName) {
    const route = typeof routeOrName === 'string' ? { name: routeOrName } : (routeOrName || {});
    const chain = findActiveChain(groups, route.name);
    if (!chain) return [];

    const items = [
        { label: chain.group.label, to: null },
        { label: chain.item.label, to: null }
    ];

    const tail = breadcrumbTail(route, chain.item);
    if (tail && tail !== chain.item.label) {
        // La página del menú deja de ser la actual: se puede volver a ella.
        items[1].to = chain.item.route || null;
        items.push({ label: tail, to: null });
    }

    return items;
}

/**
 * Keys of the toggle parents that must be expanded for the active route.
 * @param {Array<object>} groups
 * @param {string} routeName
 * @returns {Array<string>}
 */
export function collectExpandableKeys(groups = [], routeName) {
    const chain = findActiveChain(groups, routeName);
    if (!chain) return [];
    return chain.ancestors.filter(ancestor => !!ancestor.key).map(ancestor => ancestor.key);
}

/**
 * Admin sidebar navigation: visible groups, active chain, breadcrumb and
 * submenu expansion, wired to the store and the current route.
 *
 * @param {object} [options]
 * @param {boolean} [options.tracking] start the badge tracking (families
 * count fetch + visibilitychange + mensajes + echo). Only the sidebar
 * passes `true`; the header reuses the model without tracking.
 */
export function useAdminNavigation(options = {}) {
    const tracking = options.tracking === true;
    const route = useRoute();
    const instance = getCurrentInstance();
    const numMensajes = ref(0);
    const pendingFamilias = getPendingCountFamilias();
    let echoChannel = null;

    /*
     * `useRoute()` devuelve un snapshot del $route del momento del setup: al
     * navegar dentro del SPA esa referencia NO cambia, así que el ítem activo
     * y el breadcrumb quedarían congelados en la ruta inicial. Leer el getter
     * reactivo del instance mantiene ambos en sync en cada navegación (mismo
     * gotcha documentado en useListaSelection/useBasesIndex). El snapshot
     * queda como fallback para contextos sin router (unit tests).
     */
    const currentRoute = computed(() => {
        const vm = instance && instance.proxy;
        if (vm && vm.$route) return vm.$route;
        return route || null;
    });

    const routeName = computed(() => (currentRoute.value && currentRoute.value.name) || null);

    const navContext = computed(() => ({
        permisos: store.getters.getPermisos,
        configuracion: store.getters.getConfig,
        manager: store.getters.getFooter,
        teatro: store.getters.getTeatro,
        user: store.getters.getUser,
        listaId: store.getters.getListaId,
        listaAbonados: store.getters.getListaAbonados,
        pendingFamiliasCount: pendingFamilias.value,
        numMensajes: numMensajes.value
    }));

    const visibleGroups = computed(() => visibleAdminNavGroups(buildAdminNavGroups(navContext.value)));
    const activeChain = computed(() => findActiveChain(visibleGroups.value, routeName.value));
    const activeKeys = computed(() => {
        const keys = {};
        if (activeChain.value) {
            keys[activeChain.value.item.key] = true;
            activeChain.value.ancestors.forEach(ancestor => {
                if (ancestor.key) keys[ancestor.key] = true;
            });
        }
        return keys;
    });
    const breadcrumbItems = computed(() => resolveBreadcrumb(visibleGroups.value, currentRoute.value));

    const expandedKeys = ref({});
    const isExpanded = key => !!expandedKeys.value[key];

    function toggleExpanded(key) {
        expandedKeys.value = { ...expandedKeys.value, [key]: !expandedKeys.value[key] };
    }

    function expandForRoute(routeName) {
        const next = { ...expandedKeys.value };
        collectExpandableKeys(visibleGroups.value, routeName).forEach(key => {
            next[key] = true;
        });
        expandedKeys.value = next;
    }

    watch(
        routeName,
        name => {
            if (name) expandForRoute(name);
        },
        { immediate: true }
    );

    function fetchPendingFamilias() {
        fetchPendingCountFamilias().catch(() => {
            // Silencioso: el item no se muestra si el count queda en 0.
        });
    }

    function fetchNumMensajes() {
        store.dispatch(config.getVuexAlias('back.mensajeria.notifications'), { filters: {} })
            .then(res => {
                numMensajes.value = numMensajes.value + ((res.data && res.data.numMensaje) || 0);
            })
            .catch(() => {
                // Silencioso: sin badge si el endpoint falla.
            });
    }

    function handleVisibilityChange() {
        if (!document.hidden) fetchPendingFamilias();
    }

    function subscribeEcho() {
        const echo = instance && instance.proxy && instance.proxy.$echo;
        const user = store.getters.getUser;
        if (!echo || !user || !user.id) return;
        try {
            echoChannel = echo.channel('manager.incidencia.' + user.id);
            echoChannel.on('sum.incidencia', () => {
                numMensajes.value = numMensajes.value + 1;
            });
        } catch (error) {
            echoChannel = null;
        }
    }

    function startTracking() {
        fetchPendingFamilias();
        fetchNumMensajes();
        subscribeEcho();
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    function stopTracking() {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (echoChannel) {
            try {
                echoChannel.stopListening('sum.incidencia');
            } catch (error) {
                // Canal ya cerrado.
            }
            echoChannel = null;
        }
    }

    if (tracking && getCurrentInstance()) {
        onMounted(startTracking);
        onUnmounted(stopTracking);
    }

    return {
        groups: visibleGroups,
        activeChain,
        activeKeys,
        isItemActive: key => !!activeKeys.value[key],
        breadcrumbItems,
        expandedKeys,
        isExpanded,
        toggleExpanded,
        numMensajes
    };
}
